import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Award, BookOpen, Check, ChevronRight, Clock, Crown, LoaderCircle, RotateCcw, Timer, Trophy, X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EASE } from "@/components/landing/Reveal";
import { ShareSuccessButton } from "@/components/dashboard/ShareSuccess";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

function ModeCard({ icon: Icon, title, points, cta, onStart, accent, disabled, note, testid }) {
  return (
    <div className={`card-integra flex flex-col p-6 ${accent.ring}`} data-testid={testid}>
      <span className={`flex h-12 w-12 items-center justify-center rounded-2xl ${accent.bg}`}><Icon className={`h-6 w-6 ${accent.text}`} /></span>
      <h2 className="mt-4 text-base font-extrabold text-slate-900">{title}</h2>
      <ul className="mt-3 flex-1 space-y-1.5">
        {points.map((p) => (
          <li key={p} className="flex items-start gap-2 text-xs text-slate-500"><Check className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${accent.text}`} /> {p}</li>
        ))}
      </ul>
      <button onClick={onStart} disabled={disabled} className={`mt-5 rounded-full py-3 text-xs font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50 ${accent.btn}`} data-testid={`${testid}-start`}>
        {cta}
      </button>
      {note && <p className="mt-2 text-center text-[10px] text-slate-400">{note}</p>}
    </div>
  );
}

function QuizRunner({ quiz, onFinish, onAbort }) {
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selected, setSelected] = useState(null);
  const [remaining, setRemaining] = useState(quiz.duration);
  const [submitting, setSubmitting] = useState(false);
  const answersRef = useRef({});

  const submit = async (finalAnswers) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/quiz/attempts/${quiz.attempt_id}/submit`, { answers: finalAnswers });
      onFinish(data);
    } catch {
      toast.error("Impossible de soumettre vos réponses.");
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (quiz.duration == null) return;
    const t = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(t);
          toast.warning("Temps écoulé ! Vos réponses sont soumises automatiquement.");
          submit(answersRef.current);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const q = quiz.questions[index];
  const last = index === quiz.questions.length - 1;

  const next = () => {
    const updated = { ...answers, [q.id]: selected };
    setAnswers(updated);
    answersRef.current = updated;
    setSelected(null);
    if (last) submit(updated);
    else setIndex((i) => i + 1);
  };

  return (
    <div className="mx-auto max-w-2xl" data-testid="quiz-runner">
      <div className="mb-5 flex items-center justify-between">
        <button onClick={onAbort} className="text-xs font-semibold text-slate-400 hover:text-rouge" data-testid="quiz-abort">Abandonner</button>
        <div className="flex items-center gap-3">
          {quiz.duration != null && (
            <span className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ${remaining <= 120 ? "bg-rose-50 text-rouge" : "bg-bleu-light text-bleu"}`} data-testid="quiz-timer">
              <Timer className="h-3.5 w-3.5" /> {fmt(remaining)}
            </span>
          )}
          <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-500" data-testid="quiz-counter">{index + 1}/{quiz.questions.length}</span>
        </div>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-bleu transition-all duration-500" style={{ width: `${(index / quiz.questions.length) * 100}%` }} />
      </div>

      <motion.div key={q.id} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, ease: EASE }} className="card-integra mt-5 p-6 sm:p-8">
        <p className="flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-bleu">
          {q.theme}
          {q.category === "mise_en_situation" && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[9px] font-bold normal-case tracking-normal text-ambre" data-testid="quiz-mes-badge">Mise en situation</span>
          )}
        </p>
        <h2 className="mt-2 text-base font-extrabold leading-snug text-slate-900 sm:text-lg" data-testid="quiz-question">{q.question}</h2>
        <div className="mt-5 space-y-2.5">
          {q.options.map((opt, oi) => (
            <button
              key={oi}
              onClick={() => setSelected(oi)}
              className={`flex w-full items-center gap-3 rounded-xl border-2 px-4 py-3.5 text-left text-sm font-medium transition-all ${
                selected === oi ? "border-bleu bg-bleu-light text-bleu" : "border-slate-100 bg-white text-slate-700 hover:border-slate-200"
              }`}
              data-testid={`quiz-option-${oi}`}
            >
              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-bold ${selected === oi ? "border-bleu bg-bleu text-white" : "border-slate-200 text-slate-400"}`}>
                {String.fromCharCode(65 + oi)}
              </span>
              {opt}
            </button>
          ))}
        </div>
        <button onClick={next} disabled={selected === null || submitting} className="btn-primary mt-6 w-full !py-3.5 disabled:opacity-40" data-testid="quiz-next-button">
          {submitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <>{last ? "Terminer et voir mes résultats" : "Question suivante"} <ChevronRight className="h-4 w-4" /></>}
        </button>
      </motion.div>
    </div>
  );
}

function Results({ result, mode, onRestart }) {
  return (
    <div className="mx-auto max-w-2xl space-y-5" data-testid="quiz-results">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: EASE }}
        className={`card-integra p-8 text-center ${result.passed ? "border-emeraude/30" : "border-rouge/20"}`}>
        <span className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${result.passed ? "bg-emerald-50" : "bg-rose-50"}`}>
          {result.passed ? <Trophy className="h-8 w-8 text-emeraude" /> : <RotateCcw className="h-8 w-8 text-rouge" />}
        </span>
        <p className="mt-4 font-display text-4xl font-extrabold text-slate-900" data-testid="quiz-score">{result.percentage}%</p>
        <p className="mt-1 text-sm font-bold text-slate-700">{result.score}/{result.total} bonnes réponses</p>
        <p className={`mt-3 inline-flex rounded-full px-4 py-1.5 text-xs font-bold ${result.passed ? "bg-emerald-50 text-emeraude" : "bg-rose-50 text-rouge"}`} data-testid="quiz-verdict">
          {result.passed ? `Réussi ! (seuil : ${result.pass_mark}%)` : `En dessous du seuil de ${result.pass_mark}% — continuez à vous entraîner`}
        </p>
        {result.passed && mode === "examen" && (
          <ShareSuccessButton percentage={result.percentage} score={result.score} total={result.total} />
        )}
        <button onClick={onRestart} className="btn-secondary mx-auto mt-6 !px-6 !py-2.5" data-testid="quiz-back-home">Retour aux quiz</button>
      </motion.div>

      {result.parcours_updated && (
        <div className="card-integra flex items-center gap-3 border-emeraude/30 bg-emerald-50/60 p-4" data-testid="quiz-parcours-updated">
          <Trophy className="h-5 w-5 shrink-0 text-emeraude" />
          <p className="text-xs font-semibold text-slate-700">
            Examen blanc réussi : l'étape <strong>« Simulation entretien & examen blanc »</strong> de votre parcours vient d'être validée automatiquement. 🎉
          </p>
        </div>
      )}

      {result.themes?.length > 0 && (
        <div className="card-integra p-6" data-testid="quiz-theme-stats">
          <h2 className="text-sm font-bold text-slate-900">Scores par thématique</h2>
          <p className="mt-0.5 text-[11px] text-slate-400">Ciblez vos révisions sur les thématiques les plus faibles.</p>
          <div className="mt-4 space-y-3.5">
            {result.themes.map((t) => {
              const pct = t.total ? Math.round((t.correct / t.total) * 100) : 0;
              return (
                <div key={t.theme} data-testid="quiz-theme-bar">
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span className="font-medium text-slate-600">{t.theme}</span>
                    <span className="shrink-0 font-bold text-slate-900">{t.correct}/{t.total} · {pct}%</span>
                  </div>
                  <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className={`h-full rounded-full transition-all duration-700 ${pct >= 80 ? "bg-emeraude" : pct >= 50 ? "bg-ambre" : "bg-rouge"}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">Corrections détaillées</h2>
      {result.results.map((r, i) => (
        <div key={r.id} className={`card-integra p-5 ${r.is_correct ? "" : "border-rouge/20"}`} data-testid={`quiz-correction-${i}`}>
          <div className="flex items-start gap-3">
            <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${r.is_correct ? "bg-emerald-50 text-emeraude" : "bg-rose-50 text-rouge"}`}>
              {r.is_correct ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{i + 1}. {r.theme}</p>
              <p className="mt-1 text-sm font-bold text-slate-900">{r.question}</p>
              {!r.is_correct && r.chosen != null && (
                <p className="mt-2 text-xs text-rouge">Votre réponse : {r.options[r.chosen]}</p>
              )}
              {r.chosen == null && <p className="mt-2 text-xs text-ambre">Non répondu</p>}
              <p className="mt-1 text-xs font-semibold text-emeraude">Bonne réponse : {r.options[r.correct]}</p>
              <p className="mt-2 rounded-xl bg-slate-50 p-3 text-xs leading-relaxed text-slate-600">{r.explanation}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ExamenQuiz() {
  const [view, setView] = useState("home");
  const [history, setHistory] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [result, setResult] = useState(null);
  const [starting, setStarting] = useState(false);

  const loadHistory = () => api.get("/quiz/attempts").then((r) => setHistory(r.data)).catch(() => setHistory(false));
  useEffect(() => { loadHistory(); }, []);

  const start = async (mode, theme) => {
    setStarting(true);
    try {
      const { data } = await api.post("/quiz/start", { mode, theme });
      setQuiz(data);
      setView("quiz");
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error(err.response.data.detail, {
          action: { label: "Passer à Premium", onClick: () => (window.location.href = "/dashboard/parametres") },
        });
      } else toast.error("Impossible de démarrer le quiz.");
    } finally {
      setStarting(false);
    }
  };

  const finish = (res) => { setResult(res); setView("results"); loadHistory(); };
  const backHome = () => { setView("home"); setQuiz(null); setResult(null); loadHistory(); };

  if (view === "quiz" && quiz) return <QuizRunner quiz={quiz} onFinish={finish} onAbort={backHome} />;
  if (view === "results" && result) return <Results result={result} mode={quiz?.mode} onRestart={backHome} />;

  const stats = history?.stats;
  const examQuota = history?.exam_quota;

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="page-examen-quiz">
      <div>
        <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Examen & Quiz</h1>
        <p className="mt-1 text-sm text-slate-500">Entraînez-vous au format officiel de l'examen civique : 40 questions, 45 minutes, seuil de 32/40.</p>
      </div>

      {stats?.count > 0 && (
        <div className="grid grid-cols-3 gap-4" data-testid="quiz-stats">
          {[
            { label: "Quiz passés", value: stats.count },
            { label: "Meilleur score", value: `${stats.best}%` },
            { label: "Score moyen", value: `${stats.average}%` },
          ].map((s) => (
            <div key={s.label} className="card-integra p-4 text-center">
              <p className="font-display text-2xl font-extrabold text-bleu">{s.value}</p>
              <p className="mt-0.5 text-[11px] font-semibold text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <ModeCard
          icon={BookOpen} title="Quiz d'entraînement"
          points={["10 questions aléatoires", "Sans limite de temps", "Corrections détaillées", "Illimité, même en compte gratuit"]}
          cta={starting ? "Préparation…" : "Lancer un entraînement"} onStart={() => start("entrainement")} disabled={starting}
          accent={{ bg: "bg-bleu-light", text: "text-bleu", btn: "bg-bleu hover:bg-bleu-dark", ring: "" }}
          testid="mode-entrainement"
        />
        <ModeCard
          icon={Award} title="Examen blanc officiel"
          points={["40 questions au format officiel (28 connaissances + 12 mises en situation)", "Chronométré : 45 minutes maximum", "Seuil officiel : 32/40 (80 %)", "Les 5 thématiques du programme officiel"]}
          cta={starting ? "Préparation…" : "Passer un examen blanc"} onStart={() => start("examen")} disabled={starting}
          accent={{ bg: "bg-rose-50", text: "text-rouge", btn: "bg-rouge hover:bg-red-700", ring: "ring-1 ring-rouge/10" }}
          note={examQuota && !examQuota.unlimited
            ? `${examQuota.remaining}/${examQuota.limit} examen gratuit restant aujourd'hui`
            : examQuota?.unlimited ? "Illimité avec votre compte Premium" : null}
          testid="mode-examen"
        />
      </div>

      <div className="card-integra p-5" data-testid="quiz-official-info">
        <p className="text-xs font-bold uppercase tracking-wider text-bleu">Examen civique officiel — depuis le 1er janvier 2026</p>
        <p className="mt-2 text-xs leading-relaxed text-slate-600">
          Obligatoire pour toute première demande de carte de séjour pluriannuelle (CSP), de carte de résident (CR) et pour la
          naturalisation : 40 QCM sur support numérique, 45 minutes maximum, seuil de réussite à 32/40. Niveaux distincts selon le
          titre visé (CSP, CR, naturalisation) et niveau de français exigé : A2 (CSP), B1 (CR), B2 (naturalisation).
          L'attestation de réussite doit être obtenue avant le dépôt de votre dossier.
        </p>
        <p className="mt-2 text-xs leading-relaxed text-slate-600">
          Inscription auprès des centres agréés — liste officielle des questions de connaissances et annuaire des centres sur{" "}
          <a href="https://formation-civique.interieur.gouv.fr/" target="_blank" rel="noreferrer" className="font-semibold text-bleu underline" data-testid="quiz-official-link">
            formation-civique.interieur.gouv.fr
          </a>. Méfiez-vous des sites payants non officiels.
        </p>
      </div>

      <div className="card-integra p-5" data-testid="quiz-thematique-section">
        <h2 className="text-sm font-bold text-slate-900">Quiz ciblé par thématique</h2>
        <p className="mt-0.5 text-[11px] text-slate-400">10 questions sur une seule thématique — idéal pour travailler vos points faibles repérés dans les statistiques.</p>
        <div className="mt-3.5 flex flex-wrap gap-2">
          {["Principes et valeurs de la République", "Système institutionnel et politique", "Droits et devoirs", "Histoire, géographie et culture", "Vie dans la société française"].map((t) => (
            <button key={t} onClick={() => start("thematique", t)} disabled={starting}
              className="rounded-full border border-bleu/25 bg-bleu-light px-4 py-2 text-xs font-semibold text-bleu transition-all hover:-translate-y-0.5 hover:bg-bleu hover:text-white disabled:opacity-50"
              data-testid="quiz-theme-chip">
              {t}
            </button>
          ))}
        </div>
      </div>

      {examQuota && !examQuota.unlimited && (
        <Link to="/dashboard/parametres" className="flex items-center justify-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs font-bold text-amber-700 transition-colors hover:bg-amber-100" data-testid="quiz-premium-banner">
          <Crown className="h-4 w-4" /> Examens blancs illimités avec Premium — 7,99 €/mois
        </Link>
      )}

      <section data-testid="quiz-history">
        <h2 className="mb-2.5 text-xs font-bold uppercase tracking-wider text-slate-400">Historique</h2>
        {history === null && <div className="flex justify-center py-8"><LoaderCircle className="h-5 w-5 animate-spin text-slate-300" /></div>}
        {Array.isArray(history?.attempts) && history.attempts.length === 0 && (
          <p className="card-integra p-5 text-sm text-slate-500">Aucun quiz passé pour le moment. Lancez votre premier entraînement !</p>
        )}
        <ul className="space-y-2">
          {history?.attempts?.map((a) => (
            <li key={a.id} className="card-integra flex items-center gap-3.5 p-4" data-testid="quiz-history-item">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${a.mode === "examen" ? "bg-rose-50 text-rouge" : "bg-bleu-light text-bleu"}`}>
                {a.mode === "examen" ? <Award className="h-4.5 w-4.5" /> : <BookOpen className="h-4.5 w-4.5" />}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-slate-900">{a.mode === "examen" ? "Examen blanc" : a.mode === "thematique" ? `Quiz ciblé${a.theme ? ` — ${a.theme}` : ""}` : "Entraînement"} — {a.score}/{a.total}</p>
                <p className="flex items-center gap-1 text-[11px] text-slate-400"><Clock className="h-3 w-3" /> {new Date(a.finished_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}</p>
              </div>
              <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${a.passed ? "bg-emerald-50 text-emeraude" : "bg-rose-50 text-rouge"}`}>
                {a.percentage}% · {a.passed ? "Réussi" : "Échoué"}
              </span>
              {a.mode === "examen" && a.passed && (
                <ShareSuccessButton small percentage={a.percentage} score={a.score} total={a.total} finishedAt={a.finished_at} />
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
